import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";

export type ModePeriodeKebidanan = "bulanan" | "tahunan" | "rentang";

export interface FilterKebidanan {
  mode: ModePeriodeKebidanan;
  periode: string;
  tanggal_dari: string;
  tanggal_sampai: string;
  unit: string[];
  cari: string;
}

export interface OpsiUnitKebidanan {
  id: string;
  nama: string;
}

export interface ParamsKebidanan {
  mode: ModePeriodeKebidanan;
  periode_key: string;
  periode_label: string;
  periode_start: string;
  periode_end: string;
  jumlah_hari: number;
  unit: string[];
  ruang_label: string;
  opsi_unit: OpsiUnitKebidanan[];
  cari: string;
  max_pdf_baris: number;
}

export interface KolomRegisterKebidanan {
  key: string;
  jalur: string[];
  bayi: boolean;
  cek: boolean;
}

export type NilaiSelKebidanan = string | number | string[];

export interface BarisRegisterKebidanan {
  register_id: number;
  kategori: string;
  no: number;
  jumlah_bayi: number;
  [key: string]: NilaiSelKebidanan;
}

export interface MetaRegisterObstetri {
  total: number;
  page: number;
  per_page: number;
  last_page: number;
  per_ruang: Record<string, number>;
  cara_tidak_tercatat: string[];
  bayi_tidak_lengkap: string[];
}

export interface RegisterObstetriResponse {
  rows: BarisRegisterKebidanan[];
  kolom: KolomRegisterKebidanan[];
  meta: MetaRegisterObstetri;
  params: ParamsKebidanan;
}

export interface MetaRegisterGinekologi {
  total: number;
  page: number;
  per_page: number;
  last_page: number;
  per_ruang: Record<string, number>;
  meninggal_tanpa_waktu: string[];
}

export interface RegisterGinekologiResponse {
  rows: BarisRegisterKebidanan[];
  kolom: KolomRegisterKebidanan[];
  meta: MetaRegisterGinekologi;
  params: ParamsKebidanan;
}

export type JenisBarisRekap = "otomatis" | "semi" | "manual" | "turunan";

export type AgregatRekap = "jumlah" | "rata" | null;

export interface RumusRekap {
  op: "jumlah" | "kurang" | "persen";
  a?: string;
  b?: string;
  kunci?: string[];
  desimal?: number;
}

export interface DefinisiRekap {
  jenis: JenisBarisRekap;
  rumus: RumusRekap | null;
  aturan: string;
}

export interface KolomRekap {
  id: string;
  tahun: number;
  bulan: number;
  dari: string;
  sampai: string;
  penuh: boolean;
  berjalan: boolean;
  koreksi_berlaku: boolean;
}

export interface BarisBlokRekap {
  no?: string;
  key: string;
  label: string;
  keterangan: string | null;
  jenis: JenisBarisRekap;
  ubah: boolean;
  akhir: number | null;
  tebal?: boolean;
  arsir?: boolean;
}

export interface BlokRekap {
  key: string;
  judul: string | null;
  label_tahun: string;
  label_kolom: string[];
  label_akhir: string | null;
  akhir: AgregatRekap;
  baris: BarisBlokRekap[];
  judul_no?: string;
  judul_label?: string;
  judul_grup?: string[];
}

export interface LembarRekap {
  key: string;
  nama: string;
  blok: BlokRekap[];
}

export type NilaiRekap = Record<string, Record<string, number | null>>;

export interface PasienRekap {
  cm: string;
  tgl: string;
  ruang: string;
}

export interface MetaMatriksHibrida {
  total_pasien: number;
  cara_tidak_tercatat: string[];
}

export interface MatriksHibridaResponse<M extends MetaMatriksHibrida = MetaMatriksHibrida> {
  params: ParamsKebidanan;
  kolom: KolomRekap[];
  lembar: LembarRekap[];
  definisi: Record<string, DefinisiRekap>;
  rme: NilaiRekap;
  nilai: NilaiRekap;
  koreksi: Record<string, Record<string, number>>;
  versi: Record<string, number>;
  pengubah: Record<string, { oleh: string | null; pada: string | null }>;
  rincian: Record<string, Record<string, number[]>>;
  pasien: PasienRekap[];
  boleh_ubah: boolean;
  catatan: string[];
  meta: M;
}

export type RekapObstetriResponse = MatriksHibridaResponse<
  MetaMatriksHibrida & {
    sc_tanpa_indikasi: string[];
    persalinan_ganda: string[];
  }
>;

export type Rl36KebidananResponse = MatriksHibridaResponse<
  MetaMatriksHibrida & {
    total_poli: number;
    poli_tanpa_diagnosis: number;
  }
>;

export interface BulanSimpanRekap {
  periode: string;
  versi: number;
  koreksi: Record<string, number>;
}

export interface HasilSimpanRekap {
  message: string;
  bulan: {
    periode: string;
    versi: number;
    koreksi: Record<string, number>;
    rme: Record<string, number | null>;
  }[];
}

export interface KonflikRekap {
  periode: string;
  label: string;
  versi: number;
  oleh: string | null;
  pada: string | null;
}

export interface HasilSimpanMatriks {
  hasil?: HasilSimpanRekap;
  konflik?: KonflikRekap[];
}

export interface LayananMatriksHibrida {
  muat(f: FilterKebidanan): Promise<MatriksHibridaResponse>;
  simpan(bulan: BulanSimpanRekap[]): Promise<HasilSimpanMatriks>;
  pdf(f: FilterKebidanan): Promise<void>;
  excel(f: FilterKebidanan): Promise<void>;
}

interface GalatHttp {
  response?: { status?: number; data?: unknown };
}

function bacaBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function tampilkanGalat(err: GalatHttp): Promise<never> {
  const data = err.response?.data;

  if (err.response && data instanceof Blob) {
    try {
      err.response.data = JSON.parse(await bacaBlob(data));
    } catch {
      err.response.data = { message: "Berkas gagal dibuat" };
    }
  }

  ErrorAlert.showErrorAlert(err);

  return Promise.reject(err);
}

export function paramFilter(
  f: FilterKebidanan,
  tambahan: Record<string, unknown> = {}
): Record<string, unknown> {
  const params: Record<string, unknown> = { mode: f.mode, ...tambahan };

  if (f.mode === "rentang") {
    params.tanggal_dari = f.tanggal_dari;
    params.tanggal_sampai = f.tanggal_sampai;
  } else {
    params.periode = f.periode;
  }

  if (f.unit.length) params.unit = f.unit;
  if (f.cari.trim()) params.cari = f.cari.trim();

  return params;
}

export function filterSiap(f: FilterKebidanan): boolean {
  return f.mode === "rentang"
    ? !!f.tanggal_dari && !!f.tanggal_sampai
    : !!f.periode;
}

export function filterAwal(): FilterKebidanan {
  const kini = new Date();
  const bulan = String(kini.getMonth() + 1).padStart(2, "0");

  return {
    mode: "bulanan",
    periode: `${kini.getFullYear()}-${bulan}`,
    tanggal_dari: "",
    tanggal_sampai: "",
    unit: [],
    cari: ""
  };
}

function akhiranBerkas(f: FilterKebidanan): string {
  const periode =
    f.mode === "rentang" ? `${f.tanggal_dari}_${f.tanggal_sampai}` : f.periode;

  return f.unit.length ? `${periode}_${f.unit.join("-")}` : periode;
}

const TIMEOUT_LAPORAN_MS = 300000;

function unduhObjek(objek: string, nama: string): void {
  const a = document.createElement("a");
  a.href = objek;
  a.download = nama;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function bukaPdf(
  url: string,
  params: Record<string, unknown>,
  nama: string
): Promise<void> {
  const res = await http
    .getDataParam(url, {
      params,
      responseType: "blob",
      timeout: TIMEOUT_LAPORAN_MS
    })
    .catch(tampilkanGalat);

  const objek = URL.createObjectURL(
    new Blob([res.data], { type: "application/pdf" })
  );

  if (!window.open(objek, "_blank")) {
    unduhObjek(objek, nama);
  }

  setTimeout(() => URL.revokeObjectURL(objek), 60000);
}

async function unduhXlsx(
  url: string,
  params: Record<string, unknown>,
  nama: string
): Promise<void> {
  const res = await http
    .getDataParam(url, {
      params,
      responseType: "blob",
      timeout: TIMEOUT_LAPORAN_MS
    })
    .catch(tampilkanGalat);

  const objek = URL.createObjectURL(
    new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
  );
  unduhObjek(objek, nama);
  setTimeout(() => URL.revokeObjectURL(objek), 60000);
}

const URL_REGISTER_OBSTETRI = "/laporan/kebidanan/register-obstetri";

async function registerObstetri(
  f: FilterKebidanan,
  page: number,
  perPage: number
): Promise<RegisterObstetriResponse> {
  const res = await http
    .getDataParam(URL_REGISTER_OBSTETRI, {
      params: paramFilter(f, { page, per_page: perPage })
    })
    .catch(tampilkanGalat);

  return res.data;
}

function pdfRegisterObstetri(f: FilterKebidanan): Promise<void> {
  return bukaPdf(
    `${URL_REGISTER_OBSTETRI}/pdf`,
    paramFilter(f),
    `register-obstetri_${akhiranBerkas(f)}.pdf`
  );
}

function excelRegisterObstetri(f: FilterKebidanan): Promise<void> {
  return unduhXlsx(
    `${URL_REGISTER_OBSTETRI}/excel`,
    paramFilter(f),
    `register-obstetri_${akhiranBerkas(f)}.xlsx`
  );
}

const URL_REGISTER_GINEKOLOGI = "/laporan/kebidanan/register-ginekologi";

async function registerGinekologi(
  f: FilterKebidanan,
  page: number,
  perPage: number
): Promise<RegisterGinekologiResponse> {
  const res = await http
    .getDataParam(URL_REGISTER_GINEKOLOGI, {
      params: paramFilter(f, { page, per_page: perPage })
    })
    .catch(tampilkanGalat);

  return res.data;
}

function pdfRegisterGinekologi(f: FilterKebidanan): Promise<void> {
  return bukaPdf(
    `${URL_REGISTER_GINEKOLOGI}/pdf`,
    paramFilter(f),
    `register-ginekologi_${akhiranBerkas(f)}.pdf`
  );
}

function excelRegisterGinekologi(f: FilterKebidanan): Promise<void> {
  return unduhXlsx(
    `${URL_REGISTER_GINEKOLOGI}/excel`,
    paramFilter(f),
    `register-ginekologi_${akhiranBerkas(f)}.xlsx`
  );
}

const URL_REKAP_OBSTETRI = "/laporan/kebidanan/rekap-obstetri";

const URL_RL36_KEBIDANAN = "/laporan/kebidanan/rl36";

function filterRekap(f: FilterKebidanan): Record<string, unknown> {
  return paramFilter({ ...f, cari: "" });
}

function filterRl36(f: FilterKebidanan): Record<string, unknown> {
  return paramFilter({ ...f, cari: "", unit: [] });
}

async function muatMatriks<T>(
  url: string,
  params: Record<string, unknown>
): Promise<T> {
  const res = await http
    .getDataParam(url, { params, timeout: TIMEOUT_LAPORAN_MS })
    .catch(tampilkanGalat);

  return res.data;
}

async function simpanMatriks(
  url: string,
  bulan: BulanSimpanRekap[]
): Promise<HasilSimpanMatriks> {
  try {
    const res = await http.putData(url, { bulan }, { timeout: TIMEOUT_LAPORAN_MS });

    return { hasil: res.data };
  } catch (err) {
    const galat = err as GalatHttp;

    if (galat.response && galat.response.status === 409) {
      const data = galat.response.data as { konflik?: KonflikRekap[] };

      return { konflik: data && data.konflik ? data.konflik : [] };
    }

    return Promise.reject(err);
  }
}

function rekapObstetri(f: FilterKebidanan): Promise<RekapObstetriResponse> {
  return muatMatriks<RekapObstetriResponse>(URL_REKAP_OBSTETRI, filterRekap(f));
}

function simpanRekapObstetri(
  bulan: BulanSimpanRekap[]
): Promise<HasilSimpanMatriks> {
  return simpanMatriks(URL_REKAP_OBSTETRI, bulan);
}

function pdfRekapObstetri(f: FilterKebidanan): Promise<void> {
  return bukaPdf(
    `${URL_REKAP_OBSTETRI}/pdf`,
    filterRekap(f),
    `rekap-obstetri_${akhiranBerkas({ ...f, cari: "" })}.pdf`
  );
}

function excelRekapObstetri(f: FilterKebidanan): Promise<void> {
  return unduhXlsx(
    `${URL_REKAP_OBSTETRI}/excel`,
    filterRekap(f),
    `rekap-obstetri_${akhiranBerkas({ ...f, cari: "" })}.xlsx`
  );
}

function rl36Kebidanan(f: FilterKebidanan): Promise<Rl36KebidananResponse> {
  return muatMatriks<Rl36KebidananResponse>(URL_RL36_KEBIDANAN, filterRl36(f));
}

function simpanRl36Kebidanan(
  bulan: BulanSimpanRekap[]
): Promise<HasilSimpanMatriks> {
  return simpanMatriks(URL_RL36_KEBIDANAN, bulan);
}

function pdfRl36Kebidanan(f: FilterKebidanan): Promise<void> {
  return bukaPdf(
    `${URL_RL36_KEBIDANAN}/pdf`,
    filterRl36(f),
    `rl36-kebidanan_${akhiranBerkas({ ...f, cari: "", unit: [] })}.pdf`
  );
}

function excelRl36Kebidanan(f: FilterKebidanan): Promise<void> {
  return unduhXlsx(
    `${URL_RL36_KEBIDANAN}/excel`,
    filterRl36(f),
    `rl36-kebidanan_${akhiranBerkas({ ...f, cari: "", unit: [] })}.xlsx`
  );
}

export default {
  registerObstetri,
  pdfRegisterObstetri,
  excelRegisterObstetri,
  registerGinekologi,
  pdfRegisterGinekologi,
  excelRegisterGinekologi,
  rekapObstetri,
  simpanRekapObstetri,
  pdfRekapObstetri,
  excelRekapObstetri,
  rl36Kebidanan,
  simpanRl36Kebidanan,
  pdfRl36Kebidanan,
  excelRl36Kebidanan
};
